import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Shield, Eye, Database, Share2, Clock, Settings, Download, Search, ChevronDown, ChevronRight, UserCheck, Globe, AlertCircle } from 'lucide-react';
import PageLayout from '../../components/ui/PageLayout';
import Icon from '../../components/AppIcon';


const PrivacyPolicy = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [privacyPreferences, setPrivacyPreferences] = useState({
    analytics: true,
    marketing: false,
    functional: true,
    performance: true
  });

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const updatePrivacyPreference = (key, value) => {
    setPrivacyPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const privacyData = {
    lastUpdated: 'January 10, 2025',
    effectiveDate: 'January 10, 2025',
    version: '1.0',
    sections: [
      {
        id: 'information-collection',
        title: 'Information We Collect',
        icon: Database,
        summary: 'Types of personal and business data we collect to provide our services.',
        content: `
          **Personal Information:**
          - Name, email address, and contact details
          - Account credentials and authentication information
          - Payment and billing information
          - Communication preferences and history
          
          **Business Data:**
          - Lead information and contact databases
          - CRM data and customer interaction history
          - Sales funnel and campaign performance data
          - Usage analytics and platform interaction data
          
          **Technical Information:**
          - IP address, browser type, and device information
          - Cookies and similar tracking technologies
          - Log files and system performance data
          - Integration and API usage data
        `
      },
      {
        id: 'data-usage',
        title: 'How We Use Your Data',
        icon: Settings,
        summary: 'Specific purposes for which we process your personal and business information.',
        content: `
          **Service Provision:**
          - Deliver core lead generation and CRM functionality
          - Process payments and manage subscriptions
          - Provide customer support and technical assistance
          - Send service-related communications
          
          **AI and Analytics:**
          - Improve AI-powered features and recommendations
          - Generate insights and performance analytics
          - Enhance lead scoring and qualification algorithms
          - Optimize platform performance and user experience
          
          **Legal and Security:**
          - Comply with legal obligations and regulations
          - Protect against fraud, abuse, and security threats
          - Enforce our Terms of Service and policies
          - Respond to law enforcement requests when required
        `
      },
      {
        id: 'data-sharing',
        title: 'Data Sharing and Third Parties',
        icon: Share2,
        summary: 'When and with whom we share your information, including service providers.',
        content: `
          **Service Providers:**
          We share data with trusted third-party providers who help us deliver our services:
          - Cloud hosting and data storage providers
          - Payment processing partners
          - Email and communication service providers
          - Analytics and monitoring tools
          
          **Business Transfers:**
          Your data may be transferred in connection with business transactions such as mergers, acquisitions, or asset sales.
          
          **Legal Requirements:**
          We may disclose information when required by law, court order, or to protect our rights and safety.
          
          **Never Sold:**
          We do not sell your personal information to third parties for their marketing purposes.
        `
      },
      {
        id: 'data-retention',
        title: 'Data Retention and Storage',
        icon: Clock,
        summary: 'How long we keep your data and where it is stored securely.',
        content: `
          **Retention Periods:**
          - Account data: Retained while your account is active and for 2 years after closure
          - Lead and CRM data: Retained according to your preferences or until deletion request
          - Payment records: Retained for 7 years for tax and legal compliance
          - Analytics data: Aggregated data may be retained indefinitely for product improvement
          
          **Data Storage:**
          - Data is stored in secure, encrypted databases
          - We use industry-leading cloud providers with SOC 2 compliance
          - Regular backups are maintained for data recovery purposes
          - Geographic data storage varies by service region
          
          **Data Deletion:**
          You can request deletion of your data at any time through your account settings or by contacting support.
        `
      },
      {
        id: 'user-rights',
        title: 'Your Privacy Rights',
        icon: UserCheck,
        summary: 'Your rights regarding personal data under GDPR, CCPA, and other privacy laws.',
        content: `
          **Data Access Rights:**
          - Request a copy of your personal data we hold
          - Review how your data is being processed
          - Access your data in a portable format
          
          **Control and Correction:**
          - Update or correct inaccurate personal information
          - Control marketing and communication preferences
          - Opt-out of certain data processing activities
          
          **Deletion Rights:**
          - Request deletion of your personal data ("right to be forgotten")
          - Close your account and delete associated data
          - Remove specific data entries or records
          
          **Legal Protections:**
          - Rights under GDPR (EU residents)
          - Rights under CCPA (California residents)
          - Rights under other applicable privacy laws
        `
      },
      {
        id: 'cookies-tracking',
        title: 'Cookies and Tracking',
        icon: Eye,
        summary: 'How we use cookies, analytics, and other tracking technologies.',
        content: `
          **Essential Cookies:**
          - Authentication and session management
          - Security and fraud prevention
          - Basic functionality and user preferences
          
          **Analytics Cookies:**
          - Website and application usage statistics
          - Performance monitoring and optimization
          - User behavior analysis and improvements
          
          **Marketing Cookies:**
          - Personalized content and recommendations
          - Advertising effectiveness measurement
          - Social media integration features
          
          **Cookie Management:**
          You can control cookie preferences through your browser settings or our cookie management tools. Note that disabling certain cookies may affect functionality.
        `
      },
      {
        id: 'security-measures',
        title: 'Security and Protection',
        icon: Shield,
        summary: 'Technical and organizational measures to protect your data.',
        content: `
          **Technical Safeguards:**
          - End-to-end encryption for data in transit and at rest
          - Multi-factor authentication for account access
          - Regular security audits and vulnerability assessments
          - Secure API endpoints with rate limiting and monitoring
          
          **Organizational Measures:**
          - Employee access controls and training programs
          - Data processing agreements with third-party providers
          - Incident response procedures and breach notification protocols
          - Regular compliance reviews and security policy updates
          
          **Compliance Standards:**
          - SOC 2 Type II certification
          - GDPR and CCPA compliance frameworks
          - Industry-specific security requirements
          - Regular third-party security assessments
        `
      },
      {
        id: 'international-transfers',
        title: 'International Data Transfers',
        icon: Globe,
        summary: 'How we handle data transfers across international borders.',
        content: `
          **Data Processing Locations:**
          Your data may be processed in various countries where we or our service providers operate, including the United States and European Union.
          
          **Transfer Safeguards:**
          - Standard Contractual Clauses (SCCs) for EU data transfers
          - Adequacy decisions for approved countries
          - Privacy Shield successor frameworks where applicable
          - Binding Corporate Rules for internal transfers
          
          **Your Rights:**
          Regardless of processing location, you retain all privacy rights under applicable laws in your jurisdiction.
        `
      }
    ]
  };

  const filteredSections = privacyData?.sections?.filter(section =>
    section?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    section?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  ) || [];

  const handleDownloadPolicy = () => {
    window.print();
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy | LeadGen Pro</title>
        <meta name="description" content="LeadGen Pro's Privacy Policy detailing data collection, usage, sharing practices, and your privacy rights under GDPR and CCPA." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <PageLayout>
        {/* Hero Section */}
        <div className="bg-white border-b rounded-lg shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-center mb-6"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl font-bold text-gray-900 mb-4"
              >
                Privacy Policy
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xl text-gray-600 mb-6"
              >
                Transparent data handling practices and privacy protection measures
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-500"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Last Updated: {privacyData?.lastUpdated}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span>Version {privacyData?.version}</span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Privacy Dashboard */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Privacy Preferences
          </h2>
          <p className="text-gray-600 text-sm mb-6">
            Control your privacy settings and data sharing preferences.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(privacyPreferences)?.map(([key, value]) => (
              <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <div className="font-medium text-gray-900 capitalize">
                    {key} Cookies
                  </div>
                  <div className="text-sm text-gray-500">
                    {key === 'analytics' && 'Help us improve the platform'}
                    {key === 'marketing' && 'Personalized content and ads'}
                    {key === 'functional' && 'Essential platform features'}
                    {key === 'performance' && 'Performance monitoring'}
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => updatePrivacyPreference(key, e?.target?.checked)}
                    className="sr-only peer"
                    disabled={key === 'functional'}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search privacy policy..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value || '')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleDownloadPolicy}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </button>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-green-50 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Privacy Topics</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {privacyData?.sections?.map((section, index) => (
              <a
                key={section?.id}
                href={`#${section?.id}`}
                className="flex items-center gap-2 text-green-700 hover:text-green-900 py-1 text-sm transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
                {index + 1}. {section?.title}
              </a>
            ))}
          </div>
        </div>

        {/* Privacy Content */}
        <div className="bg-white rounded-lg shadow-sm border">
          {filteredSections?.map((section, index) => {
            const Icon = section?.icon;
            const isExpanded = expandedSections?.[section?.id];
            
            return (
              <motion.div
                key={section?.id}
                id={section?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border-b last:border-b-0"
              >
                <div
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleSection(section?.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {section?.title}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {section?.summary}
                        </p>
                      </div>
                    </div>
                    <div className="ml-4">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="px-6 pb-6"
                  >
                    <div className="ml-14 prose prose-gray max-w-none">
                      <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                        {section?.content}
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Data Request Section */}
        <div className="bg-green-50 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Privacy Questions or Data Requests?
          </h3>
          <p className="text-gray-600 mb-4">
            Exercise your privacy rights or get answers about our data practices.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Contact Privacy Team:</h4>
              <p>
                <a 
                  href="mailto:privacy@leadgenproai.us" 
                  className="text-green-600 hover:text-green-700"
                >
                  privacy@leadgenproai.us
                </a>
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">General Support:</h4>
              <p>
                <a 
                  href="mailto:support@leadgenproai.us" 
                  className="text-green-600 hover:text-green-700"
                >
                  support@leadgenproai.us
                </a>
              </p>
              <p>
                <a 
                  href="tel:8449781051" 
                  className="text-green-600 hover:text-green-700"
                >
                  (844) 978-1051
                </a>
              </p>
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  );
};

export default PrivacyPolicy;